import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useChatInput } from '../context/ChatInputContext';
import { FaPaperPlane, FaTimes } from 'react-icons/fa';
import memoryApi from '../api/memoryApi';
import ClearChatConfirmation from '../components/ClearChatConfirmation';

// HEIC conversion helpers (lazy loaded)
let heicToLib = null;
const loadHeicConverter = async () => {
  if (!heicToLib) {
    console.log('🔄 Loading heic-to library (HEIC image detected)');
    const mod = await import("heic-to");
    heicToLib = mod.heicTo;
  }
  return heicToLib;
};
const isHeicFormat = (file) => {
  return file.type === "image/heic" || file.type === "image/heif" ||
         file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
};
const convertHeicToJpg = async (file) => {
  if (!isHeicFormat(file)) return file;
  const heicTo = await loadHeicConverter();
  const blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.85 });
  return new File([blob], file.name.replace(/\.heic|\.heif/i, ".jpg"), { type: "image/jpeg" });
};

/**
 * EditPopup - Centered popup for editing instruction/memory
 */
const EditPopup = ({ isOpen, onSave, onCancel, title, description, value, onChange, placeholder, isSaving }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div
        className="mx-4 rounded-xl backdrop-blur-md p-6"
        style={{
          width: '70vw',
          maxWidth: '800px',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          backgroundColor: 'var(--glass-modal-bg)',
          boxShadow: 'var(--shadow-box)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h2>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg transition-colors"
            style={{
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            disabled={isSaving}
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          {description}
        </p>

        {/* Textarea */}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={8}
          className="w-full px-4 py-3 rounded-lg text-sm resize-none mb-4"
          style={{
            backgroundColor: 'var(--glass-input-bg)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--accent-color, #3b82f6)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--border-color, rgba(255,255,255,0.1))'}
          autoFocus
          disabled={isSaving}
        />

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="py-2 px-4 rounded-lg transition-all duration-200 text-sm"
            style={{
              backgroundColor: 'var(--glass-input-bg)',
              color: 'var(--text-primary)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--glass-input-bg)'}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            onClick={onSave}
            disabled={isSaving}
            className="py-2 px-4 rounded-lg transition-all duration-200 text-sm"
            style={{
              backgroundColor: isSaving ? '#9ca3af' : '#3b82f6',
              color: '#fff',
              opacity: isSaving ? 0.6 : 1,
              cursor: isSaving ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => !isSaving && (e.currentTarget.style.backgroundColor = '#2563eb')}
            onMouseLeave={(e) => !isSaving && (e.currentTarget.style.backgroundColor = '#3b82f6')}
          >
            {isSaving ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Saving...
              </div>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * ProjectHome - Project home page with chat input, sessions, and right panel
 * URL: /chatty/projects/:projectId
 *
 * Layout:
 * - Left: Back to Projects, Project Title, Chat Input (top), Session cards
 * - Right: Instruction card, Memory card
 */
const ProjectHome = ({ sidebarOpen, setSidebarOpen }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { refreshSessions } = useChatInput();
  const isDarkMode = theme === 'dark';
  const inputRef = useRef(null);

  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [instruction, setInstruction] = useState('');
  const [memory, setMemory] = useState('');
  const [editingInstruction, setEditingInstruction] = useState(false);
  const [editingMemory, setEditingMemory] = useState(false);
  const [tempInstruction, setTempInstruction] = useState('');
  const [tempMemory, setTempMemory] = useState('');
  const [savingInstruction, setSavingInstruction] = useState(false);
  const [savingMemory, setSavingMemory] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hoverSessionId, setHoverSessionId] = useState(null);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editSessionTitle, setEditSessionTitle] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
  const descriptionRef = useRef(null);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [editProjectTitle, setEditProjectTitle] = useState('');
  const [editProjectDescription, setEditProjectDescription] = useState('');
  const [savingProject, setSavingProject] = useState(false);
  const [deleteConfirmProject, setDeleteConfirmProject] = useState(false);
  const [projectFiles, setProjectFiles] = useState([]);
  const [hoverFileId, setHoverFileId] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isIndexing, setIsIndexing] = useState(false);
  const [showMobilePanel, setShowMobilePanel] = useState(false);
  const [mobileFilesExpanded, setMobileFilesExpanded] = useState(false);
  const [chatsExpanded, setChatsExpanded] = useState(false);
  const [sessionMenuPlacement, setSessionMenuPlacement] = useState({ sessionId: null, top: 0, right: 0 });
  const sessionListRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const fileInputRef = useRef(null);

  const toggleSessionMenu = (sessionId, event) => {
    event.stopPropagation();

    // Capture measurements BEFORE state update (event.currentTarget becomes null inside callback)
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const headerRect = tableHeaderRef.current?.getBoundingClientRect();
    const menuHeight = 76; // 2 buttons × 36px + 4px padding
    // eslint-disable-next-line no-unused-vars
    const menuWidth = 130;

    setHoverSessionId(prev => {
      if (prev === sessionId) {
        setSessionMenuPlacement({ sessionId: null, top: 0, right: 0 });
        return null;
      }

      // Calculate fixed position - prefer opening upward (above button)
      let top = buttonRect.top - menuHeight - 4; // 4px gap

      // Header occlusion guard: if menu would overlap header, open downward
      if (headerRect && top < headerRect.bottom) {
        top = buttonRect.bottom + 4;
      }

      // Viewport guard: if menu would be clipped at top, open downward
      if (top < 8) {
        top = buttonRect.bottom + 4;
      }

      // Viewport guard: if menu would be clipped at bottom, open upward
      if (top + menuHeight > window.innerHeight - 8) {
        top = buttonRect.top - menuHeight - 4;
      }

      // Right edge: align menu's right edge with button's right edge
      const right = window.innerWidth - buttonRect.right;

      setSessionMenuPlacement({ sessionId, top, right });
      return sessionId;
    });
  };

  const MAX_PROJECT_SIZE = 5 * 1024 * 1024; // 5MB limit for documents only (RAG), images don't count

  // Load project and sessions
  useEffect(() => {
    const loadProjectData = async () => {
      if (!user) {
        // Not logged in - redirect to login
        setLoading(false);
        navigate('/login');
        return;
      }
      if (!projectId) {
        setLoading(false);
        return;
      }

      // Show loading when switching projects
      setLoading(true);

      try {
        // Load project info and its sessions
        const [projects, projectSessions] = await Promise.all([
          memoryApi.listProjects(),
          memoryApi.getProjectSessions(projectId)
        ]);

        const currentProject = projects.find(p => p.project_id === projectId);

        if (!currentProject) {
          console.error('Project not found');
          navigate('/chatty/projects');
          return;
        }

        setProject(currentProject);
        setAllProjects(projects || []);
        setInstruction(currentProject.instruction || '');
        setMemory(currentProject.memory || '');
        setSessions(projectSessions || []);

        // Load project files from backend
        try {
          const files = await memoryApi.listProjectFiles(projectId);
          setProjectFiles(files.map(f => ({
            id: f.file_id,
            file_id: f.file_id,
            name: f.filename,
            size: f.size_bytes,
            type: f.file_type,
            preview: f.file_type === 'image' ? f.public_url : null,
            uploading: false
          })));
        } catch (fileError) {
          console.error('Error loading project files:', fileError);
        }
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [user, projectId, navigate]);

  // Check if description is long enough to be truncated (2 lines ~ 120 chars)
  useEffect(() => {
    if (project?.description) {
      // If description is longer than ~120 chars, it will likely be truncated at 2 lines
      setIsDescriptionTruncated(project.description.length > 120);
    } else {
      setIsDescriptionTruncated(false);
    }
  }, [project?.description]);

  const handleSendPrompt = async () => {
    if (!inputValue.trim() || isSending) return;

    setIsSending(true);
    const promptToSend = inputValue.trim();
    setInputValue(''); // Clear input immediately for feedback

    try {
      // Create new session in this project
      const newSession = await memoryApi.createSessionInProject('New Chat', projectId);

      // Store the prompt to send after navigation
      sessionStorage.setItem('pendingPrompt', promptToSend);

      // Refresh sidebar sessions list
      refreshSessions();

      // Navigate to the chat session
      navigate(`/chatty/${newSession.session_id}`);
    } catch (error) {
      console.error('Failed to create session:', error);
      setInputValue(promptToSend); // Restore input on error
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendPrompt();
    }
  };

  const handleSessionClick = (sessionId) => {
    navigate(`/chatty/${sessionId}`);
  };

  const handleDeleteSession = async () => {
    if (!deleteConfirmSession) return;
    try {
      await memoryApi.deleteSession(deleteConfirmSession.session_id);
      setSessions(prev => prev.filter(s => s.session_id !== deleteConfirmSession.session_id));
      setDeleteConfirmSession(null);
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  const handleRenameSession = async (sessionId, newTitle) => {
    if (!newTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    try {
      await memoryApi.renameSession(sessionId, newTitle.trim());
      setSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, title: newTitle.trim() } : s
      ));
      // Trigger sidebar refresh to sync session titles
      refreshSessions();
    } catch (error) {
      console.error('Failed to rename session:', error);
    }
    setEditingSessionId(null);
  };

  const startEditingSession = (session) => {
    setEditingSessionId(session.session_id);
    setEditSessionTitle(session.title);
  };

  const openEditProject = () => {
    setEditProjectTitle(project?.title || '');
    setEditProjectDescription(project?.description || '');
    setEditingProject(true);
    setShowProjectMenu(false);
  };

  const handleSaveProject = async () => {
    if (!editProjectTitle.trim()) return;
    setSavingProject(true);
    try {
      // Use renameProject for title/description updates
      await memoryApi.renameProject(projectId, editProjectTitle.trim(), editProjectDescription.trim());
      setProject(prev => ({
        ...prev,
        title: editProjectTitle.trim(),
        description: editProjectDescription.trim()
      }));
      setEditingProject(false);
    } catch (error) {
      console.error('Failed to update project:', error);
    } finally {
      setSavingProject(false);
    }
  };

  const handleDeleteProject = async () => {
    try {
      await memoryApi.deleteProject(projectId);
      navigate('/chatty/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  // File handling - only count documents towards 5MB limit, not images
  const getTotalDocSize = () => {
    return projectFiles
      .filter(f => f.type !== 'image')
      .reduce((sum, f) => sum + (f.size || 0), 0);
  };

  const getCapacityPercent = () => {
    return Math.min(100, Math.round((getTotalDocSize() / MAX_PROJECT_SIZE) * 100));
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['pdf'].includes(ext)) return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'docx';
    if (['txt', 'md'].includes(ext)) return 'text';
    if (['csv'].includes(ext)) return 'csv';
    if (['xlsx', 'xls'].includes(ext)) return 'excel';
    if (['py', 'c', 'sh', 'js', 'ts', 'java', 'cpp', 'h', 'go', 'rs', 'rb', 'php', 'html', 'css'].includes(ext)) return 'code';
    return 'file';
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const currentDocSize = getTotalDocSize();

    const newFiles = [];
    let runningDocSize = currentDocSize;

    for (const file of files) {
      const fileType = getFileType(file.name);

      // Only check capacity for documents, images have no limit
      if (fileType !== 'image') {
        if (runningDocSize + file.size > MAX_PROJECT_SIZE) {
          alert(`Document "${file.name}" exceeds remaining capacity. 5MB limit for documents.`);
          continue;
        }
        runningDocSize += file.size;
      }
      const fileObj = {
        id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: fileType,
        file: file, // Store for upload
        preview: null,
        uploading: true // Skeleton state
      };

      // Generate preview for images immediately (for local display)
      if (fileType === 'image') {
        (async () => {
          // Convert HEIC for preview too
          const previewFile = isHeicFormat(file) ? await convertHeicToJpg(file) : file;
          const reader = new FileReader();
          reader.onload = (ev) => {
            setProjectFiles(prev => prev.map(f =>
              f.id === fileObj.id ? { ...f, preview: ev.target.result } : f
            ));
          };
          reader.readAsDataURL(previewFile);
        })();
      }

      newFiles.push(fileObj);

      // Real S3 upload via presigned URL
      (async () => {
        try {
          // Convert HEIC to JPG if needed (iPhone images)
          let uploadFile = file;
          let uploadName = file.name;
          let uploadType = file.type || 'application/octet-stream';
          if (fileType === 'image' && isHeicFormat(file)) {
            console.log('🖼️ Converting HEIC to JPG before upload...');
            uploadFile = await convertHeicToJpg(file);
            uploadName = uploadFile.name;
            uploadType = uploadFile.type;
            console.log('✅ HEIC converted:', uploadName);
          }

          // 1. Get presigned upload URL
          const { upload_url, file_id } = await memoryApi.getUploadUrl(
            projectId,
            uploadName,
            uploadType,
            uploadFile.size
          );

          // 2. Upload directly to S3
          const uploadResponse = await fetch(upload_url, {
            method: 'PUT',
            body: uploadFile,
            headers: { 'Content-Type': uploadType }
          });

          if (!uploadResponse.ok) throw new Error('S3 upload failed');

          // 3. Confirm upload and save metadata
          await memoryApi.confirmUpload(
            projectId,
            file_id,
            uploadName,
            uploadFile.size,
            uploadType,
            fileType
          );

          // 4. Update UI - mark as uploaded with backend file_id
          setProjectFiles(prev => prev.map(f =>
            f.id === fileObj.id ? { ...f, uploading: false, file_id } : f
          ));
        } catch (error) {
          console.error('Upload failed:', error);
          // Remove failed file from list
          setProjectFiles(prev => prev.filter(f => f.id !== fileObj.id));
        }
      })();
    }

    if (newFiles.length > 0) {
      setProjectFiles(prev => [...prev, ...newFiles]);
    }

    // Reset input
    e.target.value = '';
  };

  const handleRemoveFile = async (fileId) => {
    const file = projectFiles.find(f => f.id === fileId || f.file_id === fileId);
    if (file?.file_id) {
      try {
        await memoryApi.deleteProjectFile(projectId, file.file_id);
      } catch (error) {
        console.error('Failed to delete file:', error);
        return; // Don't remove from UI if backend delete failed
      }
    }
    setProjectFiles(prev => prev.filter(f => f.id !== fileId && f.file_id !== fileId));
    setSelectedFileIds(prev => prev.filter(id => id !== fileId));
  };

  const handleDownloadFile = async (file) => {
    setDownloadingFileId(file.id);
    try {
      // Get presigned URL from backend (with auth guard)
      const result = await memoryApi.getFileDownloadUrl(projectId, file.file_id);
      if (!result.download_url) {
        console.error('No download URL returned for file:', file.name);
        return;
      }
      // Create a temporary link and click it to trigger download
      const link = document.createElement('a');
      link.href = result.download_url;
      link.download = result.filename || file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFileIds(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const selectAllFiles = () => {
    setSelectedFileIds(projectFiles.map(f => f.id));
  };

  const deselectAllFiles = () => {
    setSelectedFileIds([]);
  };

  const deleteSelectedFiles = async () => {
    // Delete each selected file from backend
    for (const fileId of selectedFileIds) {
      const file = projectFiles.find(f => f.id === fileId || f.file_id === fileId);
      if (file?.file_id) {
        try {
          await memoryApi.deleteProjectFile(projectId, file.file_id);
        } catch (error) {
          console.error('Failed to delete file:', error);
        }
      }
    }
    setProjectFiles(prev => prev.filter(f => !selectedFileIds.includes(f.id) && !selectedFileIds.includes(f.file_id)));
    setSelectedFileIds([]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;

    // Create a fake event to reuse handleFileSelect logic
    const fakeEvent = { target: { files, value: '' } };
    handleFileSelect(fakeEvent);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const openInstructionPopup = () => {
    setTempInstruction(instruction);
    setEditingInstruction(true);
  };

  const openMemoryPopup = () => {
    setTempMemory(memory);
    setEditingMemory(true);
  };

  const handleSaveInstruction = async () => {
    setSavingInstruction(true);
    try {
      await memoryApi.updateProject(projectId, { instruction: tempInstruction });
      setInstruction(tempInstruction);
      setEditingInstruction(false);
    } catch (error) {
      console.error('Failed to save instruction:', error);
    } finally {
      setSavingInstruction(false);
    }
  };

  const handleSaveMemory = async () => {
    setSavingMemory(true);
    try {
      await memoryApi.updateProject(projectId, { memory: tempMemory });
      setMemory(tempMemory);
      setEditingMemory(false);
    } catch (error) {
      console.error('Failed to save memory:', error);
    } finally {
      setSavingMemory(false);
    }
  };

  // Project navigation
  const currentIndex = allProjects.findIndex(p => p.project_id === projectId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allProjects.length - 1;
  const prevProject = hasPrev ? allProjects[currentIndex - 1] : null;
  const nextProject = hasNext ? allProjects[currentIndex + 1] : null;

  const goToPrevProject = () => {
    if (prevProject) {
      navigate(`/chatty/projects/${prevProject.project_id}`);
    }
  };

  const goToNextProject = () => {
    if (nextProject) {
      navigate(`/chatty/projects/${nextProject.project_id}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Ensure UTC dates are parsed correctly (add Z if missing)
      let normalized = dateStr;
      if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
        normalized = dateStr + 'Z';
      }
      const date = new Date(normalized);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div
        className="w-full overflow-hidden"
        style={{
          height: '100vh',
          backgroundColor: 'var(--bg-primary)'
        }}
      >
        <div
          className="w-full flex flex-col"
          style={{
            paddingTop: 'var(--navbar-height)',
            paddingBottom: 'var(--footer-height)',
            height: '100vh'
          }}
        >
          <div className="flex flex-1 overflow-hidden justify-center">
            <div className="flex w-full max-w-[1400px] pt-4">
            <div className="flex-1 p-4 lg:p-6 max-w-4xl lg:pl-16 animate-pulse">
              {/* Title skeleton */}
              <div className="flex items-center justify-center mb-2 max-w-2xl mx-auto">
                <div className="h-7 w-48 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }} />
              </div>

              {/* Nav arrows skeleton - mobile */}
              <div className="flex items-center justify-between mb-3 max-w-2xl mx-auto lg:hidden">
                <div className="h-8 w-16 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                <div className="h-4 w-12 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                <div className="h-8 w-16 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }} />
              </div>

              {/* Chat input skeleton */}
              <div className="mb-8 max-w-2xl mx-auto">
                <div className="h-12 rounded-full" style={{ backgroundColor: 'var(--bg-secondary)' }} />
              </div>

              {/* Description skeleton - mobile */}
              <div className="mb-6 max-w-2xl mx-auto lg:hidden">
                <div className="h-4 w-full rounded mb-2" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                <div className="h-4 w-3/4 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
              </div>

              {/* Chats heading skeleton */}
              <div className="h-6 w-24 rounded mb-4 pl-4 lg:pl-0" style={{ backgroundColor: 'var(--bg-secondary)' }} />

              {/* Session rows skeleton */}
              <div className="max-w-2xl mx-auto pl-4 lg:pl-0 space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3 py-3 border-b" style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}>
                    <div className="flex-1 h-4 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="w-20 h-3 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Panel Skeleton - desktop only */}
            <div className="hidden lg:block w-[450px] pt-10 pb-6 pl-0 pr-6 animate-pulse flex-shrink-0">
              <div
                className="rounded-2xl overflow-hidden"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                }}
              >
                {/* Memory skeleton */}
                <div className="p-5 border-b" style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-5 w-20 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="h-6 w-24 rounded-full" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 w-full rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="h-3 w-4/5 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="h-3 w-3/5 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  </div>
                </div>

                {/* Instructions skeleton */}
                <div className="p-5 border-b" style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-5 w-28 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="h-5 w-5 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 w-full rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="h-3 w-3/4 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  </div>
                </div>

                {/* Files skeleton */}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-5 w-16 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <div className="h-5 w-5 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  </div>
                  <div
                    className="rounded-xl p-6"
                    style={{ backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.03))' }}
                  >
                    <div className="flex justify-center gap-2 mb-4">
                      <div className="h-8 w-8 rounded" style={{ backgroundColor: 'var(--bg-primary)' }} />
                      <div className="h-8 w-8 rounded" style={{ backgroundColor: 'var(--bg-primary)' }} />
                      <div className="h-8 w-8 rounded" style={{ backgroundColor: 'var(--bg-primary)' }} />
                    </div>
                    <div className="h-3 w-3/4 mx-auto rounded" style={{ backgroundColor: 'var(--bg-primary)' }} />
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Delete Session Confirmation */}
      <ClearChatConfirmation
        isOpen={!!deleteConfirmSession}
        onConfirm={handleDeleteSession}
        onCancel={() => setDeleteConfirmSession(null)}
        title="Delete Chat"
        message={`Are you sure you want to delete "${deleteConfirmSession?.title || 'this chat'}"?`}
        description="This will permanently delete this chat and all its messages. This action cannot be undone."
        confirmLabel="Yes, delete this chat"
        buttonText="Delete Chat"
      />

      {/* Delete Project Confirmation */}
      <ClearChatConfirmation
        isOpen={deleteConfirmProject}
        onConfirm={handleDeleteProject}
        onCancel={() => setDeleteConfirmProject(false)}
        title="Delete Project"
        message={`Are you sure you want to delete "${project?.title || 'this project'}"?`}
        description={`This will permanently delete the project and all ${sessions.length} chat${sessions.length === 1 ? '' : 's'} inside it. This action cannot be undone.`}
        confirmLabel="Yes, delete this project"
        buttonText="Delete Project"
      />

      {/* Edit Project Popup */}
      {editingProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div
            className="mx-4 rounded-xl backdrop-blur-md p-6"
            style={{
              width: '70vw',
              maxWidth: '800px',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              backgroundColor: 'var(--glass-modal-bg)',
              boxShadow: 'var(--shadow-box)'
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                Edit Project
              </h2>
              <button
                onClick={() => setEditingProject(false)}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                Title
              </label>
              <input
                type="text"
                value={editProjectTitle}
                onChange={(e) => setEditProjectTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--glass-input-bg)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                  outline: 'none'
                }}
                autoFocus
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                Description
              </label>
              <textarea
                value={editProjectDescription}
                onChange={(e) => setEditProjectDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                style={{
                  backgroundColor: 'var(--glass-input-bg)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                  outline: 'none'
                }}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setEditingProject(false)}
                className="py-2 px-4 rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--glass-input-bg)',
                  color: 'var(--text-primary)'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProject}
                disabled={!editProjectTitle.trim() || savingProject}
                className="py-2 px-4 rounded-lg text-sm text-white"
                style={{
                  backgroundColor: editProjectTitle.trim() && !savingProject ? '#3b82f6' : '#6b7280',
                  opacity: editProjectTitle.trim() && !savingProject ? 1 : 0.6
                }}
              >
                {savingProject ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="w-full overflow-hidden"
        style={{
          transform: sidebarOpen ? 'translateX(280px) translateZ(0)' : 'translateX(0) translateZ(0)',
          transition: 'transform 0.3s ease',
          width: '100%',
          height: '100vh',
          willChange: 'transform'
        }}
        onClick={() => sidebarOpen && setSidebarOpen(false)}
      >
      <div
        className="w-full flex flex-col"
        style={{
          paddingTop: 'var(--navbar-height)',
          paddingBottom: 'var(--footer-height)',
          height: '100vh',
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)'
        }}
      >
        <div className="flex flex-1 overflow-hidden justify-center">
        {/* Wrapper to group main + panel and center on ultrawide */}
        <div className="flex w-full max-w-[1400px] pt-4">
        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6 overflow-y-auto overflow-x-hidden max-w-4xl lg:pl-16">
          {/* Project Title with menu */}
          <div className="mb-2 max-w-2xl mx-auto relative">
            {/* Left - back arrow (absolute positioned) */}
            <button
              onClick={() => navigate('/chatty/projects')}
              className="absolute left-0 top-0 p-1.5 rounded-md transition-colors hidden lg:block"
              style={{ color: 'var(--text-secondary)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {/* Title - centered with padding for buttons */}
            <h1 className="text-xl lg:text-2xl font-bold text-center px-10" style={{ wordBreak: 'break-word' }}>{project?.title || 'Project'}</h1>
            {/* Right buttons (absolute positioned) */}
            <div className="absolute right-0 top-0 flex gap-1">
              {/* Mobile: Info button to show panel */}
              <button
                onClick={() => setShowMobilePanel(true)}
                className="lg:hidden p-1.5 rounded-md transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <div className="relative">
              <button
                onClick={() => setShowProjectMenu(!showProjectMenu)}
                className="p-1.5 rounded-md transition-colors"
                style={{ color: 'var(--text-secondary)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </button>
              {/* Dropdown menu */}
              {showProjectMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowProjectMenu(false)}
                  />
                  <div
                    className="absolute right-0 top-full mt-1 py-1 rounded-lg shadow-lg z-50 min-w-[160px]"
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                    }}
                  >
                    <button
                      onClick={openEditProject}
                      className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors"
                      style={{ color: 'var(--text-primary)' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit details
                    </button>
                    <button
                      onClick={() => {
                        setDeleteConfirmProject(true);
                        setShowProjectMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors"
                      style={{ color: '#ef4444' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete project
                    </button>
                  </div>
                </>
              )}
              </div>
            </div>
          </div>

          {/* Project Description - desktop only above chat input */}
          {project?.description && (
            <div className="mb-6 max-w-2xl mx-auto hidden lg:block">
              <div className="relative">
                <p
                  ref={descriptionRef}
                  className={`text-sm ${descriptionExpanded ? '' : 'line-clamp-2'}`}
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {project.description}
                </p>
                {/* Fade overlay when truncated */}
                {!descriptionExpanded && isDescriptionTruncated && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-6 pointer-events-none"
                    style={{
                      background: `linear-gradient(transparent, var(--bg-primary, ${isDarkMode ? '#0a0a0a' : '#ffffff'}))`
                    }}
                  />
                )}
              </div>
              {(isDescriptionTruncated || descriptionExpanded) && (
                <button
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  className="text-xs mt-1 transition-colors"
                  style={{ color: 'var(--accent-color, #3b82f6)' }}
                >
                  {descriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}
          {!project?.description && <div className="mb-6 hidden lg:block" />}

          {/* Mobile: Floating back arrow ribbon - rendered via Portal for true fixed positioning */}
          {/* Hide when popups are open */}
          {!editingInstruction && !editingMemory && !editingProject && !showMobilePanel && !deleteConfirmProject && !deleteConfirmSession && createPortal(
            <button
              onClick={() => navigate('/chatty/projects')}
              className="lg:hidden fixed left-0 top-1/2 -translate-y-1/2 z-30 py-4 px-1 rounded-r-lg transition-colors"
              style={{
                backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.1))',
                color: 'var(--text-secondary)',
                borderTop: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                borderRight: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))'
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>,
            document.body
          )}

          {/* Project Navigation Arrows - mobile only */}
          <div className="flex items-center justify-between mb-3 max-w-2xl mx-auto lg:hidden">
            <button
              onClick={goToPrevProject}
              disabled={!hasPrev}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors"
              style={{
                color: hasPrev ? 'var(--text-secondary)' : 'var(--text-muted, rgba(128,128,128,0.3))',
                opacity: hasPrev ? 1 : 0.4
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Prev
            </button>
            <span className="text-xs" style={{ color: 'var(--text-muted, rgba(128,128,128,0.5))' }}>
              {currentIndex + 1} / {allProjects.length}
            </span>
            <button
              onClick={goToNextProject}
              disabled={!hasNext}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors"
              style={{
                color: hasNext ? 'var(--text-secondary)' : 'var(--text-muted, rgba(128,128,128,0.3))',
                opacity: hasNext ? 1 : 0.4
              }}
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Chat Input Box (at top) - reuses Chatty styling */}
          <div className="mb-8 max-w-2xl mx-auto">
            <div
              className={`chatty-input-box flex items-center gap-2 px-4 py-3 ${isSending ? 'animate-pulse' : ''}`}
              style={{
                borderRadius: 'var(--radius-3xl)',
                backgroundColor: isSending
                  ? (isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.08)')
                  : 'var(--color-bg-primary)',
                border: inputFocused
                  ? (isDarkMode ? '1px solid #3b82f6' : '1px solid #06b6d4')
                  : (isDarkMode ? '1px solid rgba(139, 90, 43, 0.3)' : '1px solid rgba(0,0,0,0.1)'),
                boxShadow: inputFocused
                  ? (isDarkMode
                      ? '0 0 20px 4px rgba(59, 130, 246, 0.3), 0 0 40px 8px rgba(59, 130, 246, 0.15)'
                      : '0 0 20px 4px rgba(147, 51, 234, 0.3), 0 0 40px 8px rgba(147, 51, 234, 0.15)')
                  : 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.3s ease'
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                placeholder="Start a new chat in this project..."
                className="flex-1 bg-transparent outline-none text-sm"
                style={{
                  color: 'var(--text-primary)',
                  opacity: isSending ? 0.5 : 1
                }}
                disabled={isSending}
              />
              <button
                onClick={handleSendPrompt}
                disabled={!inputValue.trim() || isSending}
                className={`p-2.5 rounded-full transition-all duration-200 ${
                  isSending
                    ? 'opacity-50 cursor-wait'
                    : inputValue.trim()
                      ? 'hover:scale-105 chatty-send-button-active'
                      : 'chatty-glass-button opacity-50'
                }`}
                style={{
                  color: 'var(--text-primary)'
                }}
              >
                {isSending ? (
                  <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                  </svg>
                ) : (
                  <FaPaperPlane size={12} style={{ width: '12px', height: '12px' }} />
                )}
              </button>
            </div>
          </div>

          {/* Project Description - mobile only below chat input */}
          {project?.description && (
            <div className="mb-6 max-w-2xl mx-auto pl-4 lg:pl-0 lg:hidden">
              <div className="relative">
                <p
                  className={`text-sm ${descriptionExpanded ? '' : 'line-clamp-2'}`}
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {project.description}
                </p>
                {/* Fade overlay when truncated - mobile uses same logic */}
                {!descriptionExpanded && isDescriptionTruncated && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-6 pointer-events-none"
                    style={{
                      background: `linear-gradient(transparent, var(--bg-primary, ${isDarkMode ? '#0a0a0a' : '#ffffff'}))`
                    }}
                  />
                )}
              </div>
              {(isDescriptionTruncated || descriptionExpanded) && (
                <button
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  className="text-xs mt-1 transition-colors"
                  style={{ color: 'var(--accent-color, #3b82f6)' }}
                >
                  {descriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}

          {/* Session Cards */}
          <div>
            <h2 className="text-lg font-medium mb-4 pl-4 lg:pl-0" style={{ color: 'var(--text-secondary)' }}>
              Chats ({sessions.length})
            </h2>

            {sessions.length === 0 ? (
              <div
                className="text-center py-12 rounded-xl ml-4 lg:ml-0"
                style={{
                  backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.02))',
                  color: 'var(--text-secondary)'
                }}
              >
                <svg
                  className="w-12 h-12 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ opacity: 0.5 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p className="text-sm">No chats yet. Start typing above to create your first chat.</p>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto pl-4 lg:pl-0">
                {/* Table header */}
                <div
                  ref={tableHeaderRef}
                  className="flex items-center pl-3 pr-2 lg:px-3 py-2 text-xs font-medium border-b"
                  style={{
                    color: 'var(--text-secondary)',
                    borderColor: 'var(--border-color, rgba(255,255,255,0.1))'
                  }}
                >
                  <span className="flex-1">Name</span>
                  <span className="ml-auto lg:ml-0 lg:w-32 text-right">Last Updated</span>
                  <span className="hidden lg:block w-16"></span>
                </div>
                {/* Session rows container - scrollable when expanded */}
                <div
                  ref={sessionListRef}
                  className="overflow-y-auto"
                  style={{
                    maxHeight: chatsExpanded ? '400px' : 'none',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--text-secondary) transparent'
                  }}
                >
                {/* Session rows - sorted by latest first, limited when collapsed */}
                {(() => {
                  const sortedSessions = [...sessions].sort((a, b) =>
                    (b.last_accessed || b.created_at || '').localeCompare(a.last_accessed || a.created_at || '')
                  );
                  const displaySessions = chatsExpanded || sessions.length <= 5
                    ? sortedSessions
                    : sortedSessions.slice(0, 5);
                  return displaySessions.map(session => (
                  <div
                    key={session.session_id}
                    onClick={() => editingSessionId !== session.session_id && handleSessionClick(session.session_id)}
                    className="project-session-row flex items-center pl-3 pr-2 lg:px-3 py-3 cursor-pointer transition-colors border-b"
                    style={{
                      borderColor: 'var(--border-color, rgba(255,255,255,0.1))'
                    }}
                  >
                    {/* Title */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {editingSessionId === session.session_id ? (
                        <input
                          type="text"
                          value={editSessionTitle}
                          onChange={(e) => setEditSessionTitle(e.target.value)}
                          onBlur={() => handleRenameSession(session.session_id, editSessionTitle)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameSession(session.session_id, editSessionTitle);
                            if (e.key === 'Escape') setEditingSessionId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 bg-transparent border-b outline-none text-sm"
                          style={{
                            color: 'var(--text-primary)',
                            borderColor: 'var(--accent-color, #3b82f6)'
                          }}
                          autoFocus
                        />
                      ) : (
                        <span className="text-sm truncate max-w-[120px] lg:max-w-none">{session.title}</span>
                      )}
                    </div>
                    {/* Date */}
                    <span className="ml-auto text-xs text-right whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
                      {formatDate(session.last_accessed || session.created_at)}
                    </span>
                    {/* Mobile: 3-dot menu */}
                    <div className="lg:hidden relative ml-2">
                      <button
                        onClick={(e) => toggleSessionMenu(session.session_id, e)}
                        className="p-1.5 rounded-md"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>
                      {hoverSessionId === session.session_id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHoverSessionId(null);
                            }}
                          />
                          <div
                            className="fixed py-1 rounded-lg shadow-lg z-50 min-w-[120px]"
                            style={{
                              top: sessionMenuPlacement.top,
                              right: sessionMenuPlacement.right,
                              backgroundColor: 'var(--bg-primary)',
                              border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingSession(session);
                                setHoverSessionId(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Rename
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmSession(session);
                                setHoverSessionId(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2"
                              style={{ color: '#ef4444' }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    {/* Desktop: Kebab menu */}
                    <div className="hidden lg:block relative ml-2">
                      <button
                        onClick={(e) => toggleSessionMenu(session.session_id, e)}
                        className="p-1.5 rounded-md transition-all project-session-menu-btn"
                        style={{
                          backgroundColor: 'transparent',
                          color: 'var(--text-secondary)'
                        }}
                        title="Options"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>
                      {hoverSessionId === session.session_id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHoverSessionId(null);
                            }}
                          />
                          <div
                            className="fixed py-1 rounded-lg shadow-lg z-50 min-w-[120px]"
                            style={{
                              top: sessionMenuPlacement.top,
                              right: sessionMenuPlacement.right,
                              backgroundColor: 'var(--bg-primary)',
                              border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingSession(session);
                                setHoverSessionId(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2"
                              style={{ color: 'var(--text-primary)' }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Rename
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmSession(session);
                                setHoverSessionId(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2"
                              style={{ color: '#ef4444' }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  ));
                })()}
                </div>
                {/* Show more link when collapsed */}
                {!chatsExpanded && sessions.length > 5 && (
                  <button
                    onClick={() => setChatsExpanded(true)}
                    className="w-full text-center py-3 text-sm border-t"
                    style={{
                      color: 'var(--accent-color, #3b82f6)',
                      borderColor: 'var(--border-color, rgba(255,255,255,0.1))'
                    }}
                  >
                    Show {sessions.length - 5} more chats
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Memory, Instructions, Files (hidden on mobile) */}
        <div
          className="hidden lg:block w-[450px] pt-10 pb-6 pl-0 pr-6 overflow-y-auto flex-shrink-0"
        >
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
            }}
          >
            {/* Memory Section */}
            <div
              className="p-5 cursor-pointer transition-colors border-b"
              style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}
              onClick={openMemoryPopup}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.03))'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Memory</h3>
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
                  style={{
                    backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                    color: 'var(--text-secondary)'
                  }}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Only you
                </div>
              </div>
              <p
                className="text-sm line-clamp-5"
                style={{ color: memory ? 'var(--text-muted, rgba(128,128,128,0.6))' : 'var(--text-secondary)' }}
              >
                {memory || 'Project memory will show here after a few chats.'}
              </p>
            </div>

            {/* Instructions Section */}
            <div
              className="p-5 cursor-pointer transition-colors border-b"
              style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}
              onClick={openInstructionPopup}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.03))'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Instructions</h3>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{
                    color: 'var(--text-secondary)',
                    transform: editingInstruction ? 'rotate(45deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <p
                className="text-sm line-clamp-5"
                style={{ color: instruction ? 'var(--text-muted, rgba(128,128,128,0.6))' : 'var(--text-secondary)' }}
              >
                {instruction || "Add instructions to tailor Chatty's responses"}
              </p>
            </div>

            {/* Files Section */}
            <div
              className="p-5"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                outline: isDraggingFile ? '2px dashed var(--accent-color, #3b82f6)' : 'none',
                outlineOffset: '-4px',
                borderRadius: '12px',
                transition: 'outline 0.15s ease'
              }}
            >
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,.md,.csv,.jpg,.jpeg,.png,.gif,.webp,.py,.c,.sh,.js,.ts,.java,.cpp,.h,.go,.rs,.rb,.php,.html,.css"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">Files</h3>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-1 rounded-md transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Capacity bar - only show when documents exist (images don't count) */}
              {projectFiles.some(f => f.type !== 'image') && (
                <div className="mb-3">
                  <div
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.1))' }}
                  >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${getCapacityPercent()}%`,
                        backgroundColor: getCapacityPercent() > 90 ? '#ef4444' : '#3b82f6'
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {getCapacityPercent()}% of document capacity used
                    </p>
                    {isIndexing && (
                      <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                        Indexing
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Selection toolbar */}
              {selectedFileIds.length > 0 && (
                <div
                  className="flex items-center gap-3 mb-3 px-3 py-2 rounded-lg"
                  style={{ backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))' }}
                >
                  <button
                    onClick={selectedFileIds.length === projectFiles.length ? deselectAllFiles : selectAllFiles}
                    className="w-5 h-5 rounded flex items-center justify-center"
                    style={{
                      backgroundColor: selectedFileIds.length === projectFiles.length ? '#3b82f6' : 'transparent',
                      border: '2px solid #3b82f6'
                    }}
                  >
                    {selectedFileIds.length === projectFiles.length && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    {selectedFileIds.length} selected
                  </span>
                  <button
                    onClick={deleteSelectedFiles}
                    className="p-1.5 rounded-md transition-colors"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  <div className="flex-1" />
                  <button
                    onClick={deselectAllFiles}
                    className="p-1 rounded-md transition-colors"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {projectFiles.length === 0 ? (
                /* Empty state */
                <div
                  className="rounded-xl p-6 text-center cursor-pointer transition-colors"
                  style={{ backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.03))' }}
                  onClick={() => fileInputRef.current?.click()}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.06))'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.03))'}
                >
                  <div className="flex justify-center mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}>
                    <svg className="w-8 h-8 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <svg className="w-8 h-8 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <svg className="w-8 h-8 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Add PDFs, documents, or other text to reference in this project.
                  </p>
                </div>
              ) : (
                /* File grid - 2 columns, scrollable container */
                <div
                  className="overflow-y-auto pr-1"
                  style={{
                    maxHeight: '280px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--text-secondary) transparent'
                  }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    {projectFiles.map(file => (
                    <div
                      key={file.id}
                      className="relative rounded-lg overflow-hidden transition-all"
                      style={{
                        backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                        border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                      }}
                      onMouseEnter={() => setHoverFileId(file.id)}
                      onMouseLeave={() => setHoverFileId(null)}
                    >
                      {/* Download button - show when hovering and not selecting */}
                      {selectedFileIds.length === 0 && !file.uploading && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (downloadingFileId !== file.id) {
                              handleDownloadFile(file);
                            }
                          }}
                          className="absolute top-1.5 left-1.5 p-1 rounded-full z-10 transition-opacity"
                          style={{
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            opacity: hoverFileId === file.id || downloadingFileId === file.id ? 1 : 0
                          }}
                          title={downloadingFileId === file.id ? "Preparing..." : "Download"}
                          disabled={downloadingFileId === file.id}
                        >
                          {downloadingFileId === file.id ? (
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          )}
                        </button>
                      )}
                      {/* Remove button - show when hovering and not selecting */}
                      {selectedFileIds.length === 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(file.id);
                          }}
                          className="absolute top-1.5 right-1.5 p-1 rounded-full z-10 transition-opacity"
                          style={{
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            opacity: hoverFileId === file.id ? 1 : 0
                          }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}

                      {/* Selection checkbox */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFileSelection(file.id);
                        }}
                        className="absolute bottom-2 right-2 w-5 h-5 rounded flex items-center justify-center z-10 transition-opacity"
                        style={{
                          backgroundColor: selectedFileIds.includes(file.id) ? '#3b82f6' : 'rgba(0,0,0,0.4)',
                          border: selectedFileIds.includes(file.id) ? 'none' : '2px solid rgba(255,255,255,0.5)',
                          opacity: selectedFileIds.length > 0 || hoverFileId === file.id ? 1 : 0
                        }}
                      >
                        {selectedFileIds.includes(file.id) && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>

                      {/* Card content - 4:3 landscape aspect ratio */}
                      <div className="aspect-[4/3]">
                        {file.uploading ? (
                          /* Skeleton loading state */
                          <div className="w-full h-full p-3 flex flex-col animate-pulse">
                            <div className="h-3 rounded w-3/4 mb-2" style={{ backgroundColor: 'var(--bg-primary)' }} />
                            <div className="h-2 rounded w-1/2 mb-auto" style={{ backgroundColor: 'var(--bg-primary)' }} />
                            <div className="h-4 rounded w-12" style={{ backgroundColor: 'var(--bg-primary)' }} />
                          </div>
                        ) : file.type === 'image' && file.preview ? (
                          /* Image preview */
                          <img
                            src={file.preview}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          /* Document card */
                          <div className="w-full h-full p-3 flex flex-col justify-between">
                            <div>
                              <p className="text-xs font-medium line-clamp-2 mb-1" style={{ color: 'var(--text-primary)' }}>
                                {file.name}
                              </p>
                              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded self-start"
                              style={{
                                backgroundColor: 'var(--bg-primary)',
                                color: 'var(--text-secondary)'
                              }}
                            >
                              {(file.type || 'file').toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>

        {/* Instruction Edit Popup */}
        <EditPopup
          isOpen={editingInstruction}
          onSave={handleSaveInstruction}
          onCancel={() => setEditingInstruction(false)}
          title="Set project instructions"
          description="Provide Chatty with relevant instructions and information for chats within this project. This will work alongside user preferences and the selected style in a chat."
          value={tempInstruction}
          onChange={setTempInstruction}
          placeholder="Enter instructions for how Chatty should respond in this project..."
          isSaving={savingInstruction}
        />

        {/* Memory Edit Popup */}
        <EditPopup
          isOpen={editingMemory}
          onSave={handleSaveMemory}
          onCancel={() => setEditingMemory(false)}
          title="Edit project memory"
          description="Project memory helps Chatty remember important context across all chats in this project."
          value={tempMemory}
          onChange={setTempMemory}
          placeholder="Enter memory notes for this project..."
          isSaving={savingMemory}
        />

        {/* Mobile Panel Overlay */}
        {showMobilePanel && (
          <div
            className="lg:hidden fixed inset-0 z-50 flex flex-col"
            style={{
              backgroundColor: 'var(--bg-primary)',
              paddingTop: 'var(--navbar-height)'
            }}
          >
            {/* Header */}
            <div
              className="relative flex items-center justify-center px-4 py-3 border-b"
              style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}
            >
              <h2 className="text-lg font-bold">Project Info</h2>
              <button
                onClick={() => setShowMobilePanel(false)}
                className="absolute right-4 p-2 rounded-lg"
                style={{ color: 'var(--text-secondary)' }}
              >
                <FaTimes size={16} />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Memory Section */}
              <div
                className="rounded-xl p-4 mb-3 cursor-pointer"
                style={{
                  backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                }}
                onClick={() => {
                  setShowMobilePanel(false);
                  openMemoryPopup();
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Memory</h3>
                  <div
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs"
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-secondary)'
                    }}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Only you
                  </div>
                </div>
                <p
                  className="text-sm line-clamp-4"
                  style={{ color: memory ? 'var(--text-muted, rgba(128,128,128,0.6))' : 'var(--text-secondary)' }}
                >
                  {memory || 'Project memory will show here after a few chats.'}
                </p>
              </div>

              {/* Instructions Section */}
              <div
                className="rounded-xl p-4 mb-3 cursor-pointer"
                style={{
                  backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                }}
                onClick={() => {
                  setShowMobilePanel(false);
                  openInstructionPopup();
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Instructions</h3>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <p
                  className="text-sm line-clamp-4"
                  style={{ color: instruction ? 'var(--text-muted, rgba(128,128,128,0.6))' : 'var(--text-secondary)' }}
                >
                  {instruction || "Add instructions to tailor Chatty's responses"}
                </p>
              </div>

              {/* Files Section */}
              <div
                className="rounded-xl p-4"
                style={{
                  backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">Files</h3>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1 rounded-md"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>

                {projectFiles.length === 0 ? (
                  <div
                    className="rounded-lg p-4 text-center"
                    style={{ backgroundColor: 'var(--bg-primary)' }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Add PDFs, documents, or images to reference in this project.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Accordion toggle - show when 3+ files */}
                    {projectFiles.length >= 3 && (
                      <button
                        onClick={() => setMobileFilesExpanded(!mobileFilesExpanded)}
                        className="w-full flex items-center justify-between py-1 text-xs"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        <span>{projectFiles.length} files</span>
                        <svg
                          className={`w-4 h-4 transition-transform ${mobileFilesExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}

                    {/* File list - show all when expanded or <3 files, otherwise show first 2 */}
                    <div
                      className="space-y-2 overflow-y-auto pr-1"
                      style={{
                        maxHeight: mobileFilesExpanded ? '300px' : 'none',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'var(--text-secondary) transparent'
                      }}
                    >
                      {(mobileFilesExpanded || projectFiles.length < 3 ? projectFiles : projectFiles.slice(0, 2)).map(file => (
                        <div
                          key={file.id}
                          className={`flex items-center gap-3 rounded-lg p-2 ${file.uploading ? 'animate-pulse' : ''}`}
                          style={{
                            backgroundColor: 'var(--bg-primary)',
                          }}
                        >
                          {/* Thumbnail or type icon */}
                          <div
                            className="w-10 h-10 rounded flex-shrink-0 flex items-center justify-center overflow-hidden"
                            style={{ backgroundColor: 'var(--bg-secondary)' }}
                          >
                            {file.uploading ? (
                              /* Uploading spinner */
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: 'var(--accent-color, #3b82f6)' }}>
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : file.type === 'image' && file.preview ? (
                              <img src={file.preview} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--text-secondary)' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            )}
                          </div>

                          {/* File info */}
                          <div className="flex-1 min-w-0">
                            {file.uploading ? (
                              /* Uploading skeleton */
                              <>
                                <div className="h-3 rounded w-3/4 mb-1.5" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                                <div className="h-2 rounded w-1/3" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                              </>
                            ) : (
                              <>
                                <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                  {file.name}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span
                                    className="text-[10px] px-1.5 py-0.5 rounded"
                                    style={{
                                      backgroundColor: 'var(--bg-secondary)',
                                      color: 'var(--text-secondary)'
                                    }}
                                  >
                                    {file.type?.toUpperCase() || 'FILE'}
                                  </span>
                                  {file.size && (
                                    <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                                      {formatFileSize(file.size)}
                                    </span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>

                          {/* Delete button - hide while uploading */}
                          {!file.uploading && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile(file.id);
                              }}
                              className="p-2 rounded-full flex-shrink-0"
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* "Show more" hint when collapsed with 3+ files */}
                    {!mobileFilesExpanded && projectFiles.length >= 3 && (
                      <button
                        onClick={() => setMobileFilesExpanded(true)}
                        className="w-full text-center text-[11px] py-1"
                        style={{ color: 'var(--accent-color, #3b82f6)' }}
                      >
                        +{projectFiles.length - 2} more files
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
    </>
  );
};

export default ProjectHome;
